/**
 * When this sponsor next hears from us, and why they might not.
 *
 * THE SILENCES ARE THE POINT. A reminder is skipped for six different reasons
 * and most are invisible from anywhere else in the app. One in particular
 * reads exactly like a broken chaser: a sponsor who owes three versions, has
 * not been chased in a week, and the cause is something of theirs sitting in
 * our own review queue. That rule is deliberate and worth keeping, but a rule
 * nobody can see is indistinguishable from a bug.
 *
 * The whole schedule is shown rather than only the next step, because "they
 * have already had three of these" is what tells somebody a fourth is not the
 * answer.
 */

import { T } from '../lib/copyEdit.tsx';
import { useState } from 'react';

export interface ReminderStep {
  step: string;
  label: string;
  daysBefore: number;
  sent: boolean;
  isNext: boolean;
  at: string | null;
}

export interface ReminderSchedule {
  paused: boolean;
  holdUntil: string | null;
  sendHour: number;
  zone: string;
  next: { at: string | null; label: string; reason: string | null };
  steps: ReminderStep[];
}

/** Each reason a reminder is not coming, said plainly. The tone matters: most
 *  of these are correct behaviour, not faults, and should not read as errors. */
const REASONS: Record<string, { title: string; detail: string; tone: 'wait' | 'off' | 'done' }> = {
  'waiting on our review': {
    title: 'Waiting on your review',
    detail: 'Reminders resume once the versions in your queue are decided.',
    tone: 'wait',
  },
  'held after a manual message': {
    title: 'Held after a message',
    detail: 'Someone wrote to this sponsor, so the automatic ones are paused for a week.',
    tone: 'wait',
  },
  'chaser paused': {
    title: 'Turned off for this sponsor',
    detail: 'Nobody will be chased automatically. Turn it back on to resume the schedule.',
    tone: 'off',
  },
  'everything approved': {
    title: 'Everything is approved',
    detail: 'No more reminders will go out.',
    tone: 'done',
  },
  'deadline passed': {
    title: 'The deadline has passed',
    detail: 'Automatic reminders have stopped. Anything still outstanding needs a person now.',
    tone: 'off',
  },
  'schedule finished': {
    title: 'Every reminder has been sent',
    detail: 'The schedule is spent. Anything still outstanding needs a person now.',
    tone: 'off',
  },
  withdrawn: {
    title: 'Withdrawn',
    detail: 'No reminders go to a withdrawn sponsor. Reinstate them to resume the schedule where it stands.',
    tone: 'off',
  },
  'never invited': {
    title: 'Not invited yet',
    detail: 'Reminders start once the first email has gone out.',
    tone: 'off',
  },
  'no deadline set': {
    title: 'No logo deadline',
    detail: 'Reminders are anchored to the deadline. Set one on the event to start the schedule.',
    tone: 'off',
  },
};

function formatWhen(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

function formatHour(hour: number): string {
  if (hour === 0) return '12:00 am';
  if (hour < 12) return `${hour}:00 am`;
  if (hour === 12) return '12:00 pm';
  return `${hour - 12}:00 pm`;
}

export default function RemindersPanel({
  base,
  schedule,
  withdrawn = false,
  onChanged,
  onMessage,
}: {
  base: string;
  schedule: ReminderSchedule;
  /** Offers Reinstate here, where the consequence of withdrawing shows. */
  withdrawn?: boolean;
  onChanged: () => void;
  onMessage: () => void;
}) {
  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const blocked = schedule.next.reason;
  const reason = blocked ? REASONS[blocked] : null;

  async function reinstate() {
    setWorking(true);
    setProblem(null);
    try {
      const res = await fetch(`${base}/reinstate`, { method: 'POST' });
      if (!res.ok) {
        setProblem('That could not be undone.');
        return;
      }
      onChanged();
    } finally {
      setWorking(false);
    }
  }

  async function setPaused(paused: boolean) {
    setWorking(true);
    setProblem(null);
    try {
      const res = await fetch(base, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chaserPaused: paused }),
      });
      if (!res.ok) {
        setProblem('That did not save.');
        return;
      }
      onChanged();
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <h3><T k="reminders.title" d="Reminders" /></h3>
        <span className="hint">
          {blocked === 'withdrawn'
            ? 'stopped'
            : blocked
              ? reason?.tone === 'done'
                ? 'finished'
                : 'paused'
              : 'automatic'}
        </span>
      </div>

      <div className="panel-body">
        {reason ? (
          <div className={`rem-blocked rem-blocked--${reason.tone}`}>
            <b>{reason.title}</b>
            <span>{reason.detail}</span>
          </div>
        ) : (
          <div className="rem-next">
            <b>Next: {formatWhen(schedule.next.at)}</b>
            <span>
              {formatHour(schedule.sendHour)}, event time
              {schedule.next.label ? ` · ${schedule.next.label.toLowerCase()}` : ''}
            </span>
          </div>
        )}

        {/* Listed even when blocked. The history is as useful as the forecast. */}
        <ul className="rem-sched">
          {schedule.steps.map((step) => (
            <li
              key={step.step}
              className={step.isNext ? 'is-next' : step.sent ? 'is-done' : undefined}
            >
              <span
                className={`rem-dot ${step.isNext ? 'is-next' : step.sent ? 'is-done' : 'is-todo'}`}
              />
              <span className="rem-label">{step.label}</span>
              <span className="rem-when">
                {step.sent ? 'Sent' : step.at ? formatWhen(step.at).replace(/^\w+ /, '') : '—'}
              </span>
            </li>
          ))}
        </ul>

        {/* Offered exactly where the schedule runs out. The sponsor who never
            delivered is the one who needs a person, and a dead end is the
            wrong thing to show them at. */}
        {(blocked === 'deadline passed' || blocked === 'schedule finished') && (
          <button
            type="button"
            className="btn-compact btn-compact--quiet"
            style={{ width: '100%', marginTop: 'var(--sp3)' }}
            onClick={onMessage}
          >
            Message Sponsor
          </button>
        )}

        {withdrawn && (
          <button
            type="button"
            className="btn-compact btn-compact--quiet"
            style={{ width: '100%', marginTop: 'var(--sp3)' }}
            disabled={working}
            onClick={() => void reinstate()}
          >
            {working ? 'Reinstating…' : 'Reinstate Sponsor'}
          </button>
        )}

        {problem && <p className="auth-error">{problem}</p>}

        <div className="rem-pref">
          <div>
            <b><T k="reminders.toggle" d="Send reminders" /></b>
            <span>
              {schedule.paused
                ? 'Turn back on to resume the schedule.'
                : 'Turn off if you are handling this sponsor yourself.'}
            </span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={!schedule.paused}
            aria-label="Send automatic reminders"
            className={`toggle${schedule.paused ? '' : ' on'}`}
            disabled={working}
            onClick={() => void setPaused(!schedule.paused)}
          />
        </div>
      </div>
    </div>
  );
}
